import { referenceDisplay } from '@seen/core/display'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { getReferenceString } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import {
  Avatar,
  DataTable,
  DateDisplay,
  DateTimeDisplay,
  DOCUMENT_SEARCH_OPTIONS,
  DocumentInfoCell,
  DocumentReferenceSheet,
  DocumentReviewTaskBadge,
  extractDocSearchFields,
  Loader,
  PageLayout,
  ParticipantNameplate,
  PractitionerDisplay,
  RelativeDateTimeDisplay,
  ShortcutSearchInput,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useClientSideSearch,
  useCurrentPractitioner,
  useDocumentReviewTasks,
  useDocumentSignatureStatus,
  useGlobalFileUpload,
  useLabCopySecondaryFilter,
  useRead,
  useSearch,
  useUrlSearchInput,
} from '@seen/react'
import { Badge, Button } from '@seen/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { type JSX, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

const DEFAULT_PAGE_SIZE = 25

/* Pagination params are user input: anything that is not an integer at or
 * above `min` (missing, `abc`, `-1`, `2.5`) falls back to the default. */
function readIntParam(
  raw: string | null,
  min: number,
  fallback: number
): number {
  const value = Number(raw)
  return Number.isInteger(value) && value >= min ? value : fallback
}

export function DocumentsPage(): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const [searchParams, setSearchParams] = useSearchParams()
  const documentReferenceId = searchParams.get('documentReferenceId')

  /* Read independently of the list so a `?documentReferenceId=` deep link
   * opens the sheet whether or not the document is on the current page (or in
   * the current search results). */
  const documentReferenceQuery = useRead(
    ExtendedDocumentReference,
    documentReferenceId
  )
  const documentReference = documentReferenceQuery.data

  const withoutLabCopySecondaries = useLabCopySecondaryFilter()
  const { data: documents, isLoading } = useSearch(
    ExtendedDocumentReference,
    withoutLabCopySecondaries(
      ExtendedDocumentReference.byAuthor(practitioner.reference)
    )
  )

  const documentList = documents ?? []

  const { tasksByDocument: reviewTasksByDocument } =
    useDocumentReviewTasks(documentList)

  const signedDocuments = useDocumentSignatureStatus(documentList)

  const { searchInput, setSearchInput, debouncedSearch } = useUrlSearchInput({
    onDebouncedChange: (params) => {
      /* A new query re-ranks the whole result set, so whatever was on the
       * current page is no longer there. */
      params.set('pageIndex', '0')
    },
  })

  /* `documents`, not `documentList`: the hook rebuilds its index whenever the
   * array reference changes, and the `?? []` fallback is a fresh array every
   * render while the query loads. */
  const { search: searchDocuments, getExtractedFields } = useClientSideSearch(
    documents,
    {
      ...DOCUMENT_SEARCH_OPTIONS,
      extractFields: extractDocSearchFields,
      idleBuild: true,
    }
  )

  /* An empty query returns the input unchanged, preserving the server's
   * `-_lastUpdated` order; a non-empty one is MiniSearch relevance order. */
  const { items: rows, matchById } = useMemo(
    () => searchDocuments(debouncedSearch),
    [searchDocuments, debouncedSearch]
  )

  /* DataTable has `autoResetPageIndex` off, so a page past the end (a stale
   * link to a result set that has since shrunk) would render empty. Clamp to
   * the last page instead; the URL itself is left alone. */
  const pageSize = readIntParam(
    searchParams.get('pageSize'),
    1,
    DEFAULT_PAGE_SIZE
  )
  const lastPageIndex = Math.max(Math.ceil(rows.length / pageSize) - 1, 0)
  const pageIndex = Math.min(
    readIntParam(searchParams.get('pageIndex'), 0, 0),
    lastPageIndex
  )

  const showDocument = useCallback(
    (id: string): void => {
      setSearchParams((prev) => {
        prev.set('documentReferenceId', id)
        return prev
      })
    },
    [setSearchParams]
  )

  const columns = useMemo<ColumnDef<ExtendedDocumentReference>[]>(
    () => [
      {
        id: 'documentInfo',
        header: 'Document',
        cell: ({ row }) => (
          <DocumentInfoCell
            doc={row.original}
            match={matchById.get(row.original.id)}
            extractedFields={getExtractedFields(row.original.id)}
            /* No category tabs on this page, so every category the
             * document carries is worth a badge. */
            activeCategoryFilter={undefined}
            onTitleClick={() => showDocument(row.original.id)}
          />
        ),
      },
      {
        id: 'participant',
        header: 'Participant',
        accessorFn: (docRef) => docRef.subject?.reference,
        cell: ({ row }) => {
          const subject = row.original.subject
          if (isOfType(subject, 'Patient')) {
            return <ParticipantNameplate participant={subject} />
          }
          return null
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (docRef) => {
          const refString = getReferenceString(docRef.reference)
          if (reviewTasksByDocument.get(refString)) {
            return 0
          }
          if (signedDocuments.get(refString)) {
            return 1
          }
          return 2
        },
        cell: ({ row }) => {
          const refString = getReferenceString(row.original.reference)
          const reviewTask = reviewTasksByDocument.get(refString)
          const signature = signedDocuments.get(refString)
          return (
            <div className="flex flex-col gap-1">
              {reviewTask && <DocumentReviewTaskBadge task={reviewTask} />}
              {signature && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      color="green"
                      className="flex w-fit items-center gap-1"
                    >
                      <Avatar
                        profile={signature.who}
                        size="icon"
                        className="size-3"
                      />
                      Signed
                      <RelativeDateTimeDisplay
                        value={signature.recorded}
                        element="span"
                        className="text-3xs"
                      />
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    Signed by {referenceDisplay(signature.who)}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        id: 'author',
        header: 'Uploaded By',
        accessorFn: (docRef) => docRef.author?.reference,
        cell: ({ row }) => {
          const author = row.original.author
          if (isOfType(author, 'Practitioner')) {
            return <PractitionerDisplay practitioner={author} />
          }
          if (isOfType(author, 'Organization')) {
            return <span>{referenceDisplay(author)}</span>
          }
          return null
        },
      },
      {
        id: 'serviceDate',
        header: 'Service date',
        accessorFn: (docRef) => docRef.serviceDateString,
        cell: ({ row }) => (
          <DateDisplay value={row.original.serviceDateString} />
        ),
      },
      {
        id: 'date',
        header: 'Upload date',
        accessorFn: (docRef) => docRef.date,
        cell: ({ row }) => <DateTimeDisplay value={row.original.dateString} />,
      },
    ],
    [
      matchById,
      getExtractedFields,
      showDocument,
      reviewTasksByDocument,
      signedDocuments,
    ]
  )

  const { showUploadArea } = useGlobalFileUpload()

  if (isLoading) {
    return <Loader />
  }

  return (
    <PageLayout
      title="Uploaded Documents"
      actions={
        <div className="flex items-center gap-2">
          <ShortcutSearchInput
            value={searchInput}
            onChange={setSearchInput}
            className="w-48 shrink-0"
          />
          <Button variant="outline" onClick={showUploadArea}>
            Upload Documents
          </Button>
        </div>
      }
    >
      <DataTable
        columns={columns}
        data={rows}
        onRowClick={(row) => showDocument(row.id)}
        /* Paginating is also what stops DataTable virtualizing the body, so
         * every row on the page is mounted. */
        state={{ pagination: { pageIndex, pageSize } }}
        onPaginationChange={(updater) => {
          const next =
            typeof updater === 'function'
              ? updater({ pageIndex, pageSize })
              : updater
          setSearchParams((prev) => {
            prev.set('pageIndex', next.pageIndex.toString())
            prev.set('pageSize', next.pageSize.toString())
            return prev
          })
        }}
      />
      <DocumentReferenceSheet
        documentReference={documentReference}
        open={!!documentReference}
        setOpen={(open) => {
          if (!open) {
            setSearchParams((prev) => {
              prev.delete('documentReferenceId')
              return prev
            })
          }
        }}
      />
    </PageLayout>
  )
}
